import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { LabeledInfo } from 'ui-components/components/labeled-info';
import { Text, TextType } from 'ui-components/components/text';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import messages from './messages';
import style from './style.module.less';
import { TaxInfoFooter } from './tax-info-footer';
import { useEntitiesFulfillmentPlan } from '../../hooks/entities/selectors/fulfillment-plans';
import {
  ORDER_DETAILS_ACTIONS,
  useTrackOrderDetailsAction,
} from '../modals/order-details-modal/event-tracking/use-track-order-details-action';
import EditRecipientTaxIdentifiersFormProvider from '../order-details/fulfillment-plan/sections/shipment-details/tax-identifier/edit-tax-identifiers/context';
import { EditRecipientTaxIdentifiersForm } from '../order-details/fulfillment-plan/sections/shipment-details/tax-identifier/edit-tax-identifiers/edit-tax-identifiers-form';
import { ReadOnlyRecipientTaxIdentifiersContent } from '../order-details/fulfillment-plan/sections/shipment-details/tax-identifier/read-only-tax-identifiers/read-only-tax-identifiers-content';

/**
 * @param {object} props
 * @param {string} props.fulfillmentPlanId - selected fulfillmentPlanId to configure tax identifiers
 * @param {string} props.salesOrderId - selected salesOrderId to configure tax identifiers
 * @returns {JSX.Element} The rendered RecipientTaxInfoContent
 */
export const RecipientTaxInfoContent = ({ salesOrderId = '', fulfillmentPlanId = '' }) => {
  const intl = useIntl();
  const [editMode, setEditMode] = useAsyncSafeState(false);

  const onCancel = useCallback(() => {
    setEditMode(false);
  }, [setEditMode]);

  const trackEditTaxIdentifier = useTrackOrderDetailsAction(ORDER_DETAILS_ACTIONS.EDIT_ORDER_TAX_IDENTIFIER);

  const enableEditMode = useCallback(() => {
    setEditMode(true);
    trackEditTaxIdentifier();
  }, [setEditMode, trackEditTaxIdentifier]);

  const fulfillmentPlan = useEntitiesFulfillmentPlan(fulfillmentPlanId);

  return (
    <div className={style['tax-info']}>
      <LabeledInfo label={intl.formatMessage(messages.taxInfo)}>
        {editMode ? (
          <EditRecipientTaxIdentifiersFormProvider
            fulfillmentPlanId={fulfillmentPlanId}
            initialTaxIdentifiers={fulfillmentPlan?.recipientTaxIdentifiers}
            salesOrderId={salesOrderId}
          >
            <Text className={style.information} textType={TextType.Caption} value={messages.editTaxIdInformation} />
            <EditRecipientTaxIdentifiersForm />
            <TaxInfoFooter onCancel={onCancel} />
          </EditRecipientTaxIdentifiersFormProvider>
        ) : (
          <ReadOnlyRecipientTaxIdentifiersContent fulfillmentPlanId={fulfillmentPlanId} onEdit={enableEditMode} />
        )}
      </LabeledInfo>
    </div>
  );
};
